
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
  ArrowLeft,
  Loader2,
  User,
  Users,
  Sparkles,
  Check,
  ListFilter,
  CheckCircle2,
  Trash2,
  MoreVertical,
  RefreshCw,
  Megaphone,
  PenLine,
  Globe,
  Linkedin,
  Building2,
  ExternalLink,
} from 'lucide-react';
import { apiGet, apiPatch, apiDelete, apiPost } from '@/lib/api';
import { getLeadGroup, getLeadGroupLeads, deleteLeadGroup, type LeadGroup } from '@/lib/lead-groups';
import { generateEmailsPreview, generateEmailsBatch } from '@/lib/outreach';
import { useToast } from '@/hooks/useToast';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { AIProgressBar } from '@/components/AIProgressBar';
import { FormulaNode, FormulaOperator } from '@/components/FormulaBar';
import { LeadGroupPicker } from '@/components/LeadGroupPicker';
import type { Lead } from '@hypema/shared';

interface EmailPreviewItem {
  leadId: string;
  lead: { firstName: string; lastName: string; company: string; email: string };
  subject: string;
  body: string;
}

export function DraftEmailsPage() {
  const toast = useToast();
  const navigate = useNavigate();

  /* ── Selected group (from localStorage) ── */
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);

  /* ── Data ── */
  const [group, setGroup] = useState<LeadGroup | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState<any>(null);

  /* ── Phase ── */
  const [phase, setPhase] = useState<'idle' | 'preview' | 'generating'>('idle');
  const [showMissingDialog, setShowMissingDialog] = useState(false);

  /* ── Detail view ── */
  const [detailView, setDetailView] = useState<'leads' | 'sender' | 'topic' | 'pick-list' | null>(null);

  /* ── Lead selection ── */
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());

  /* ── Column filters ── */
  const [columnFilters, setColumnFilters] = useState<Record<string, Set<string>>>({});
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [pendingFilter, setPendingFilter] = useState<Set<string>>(new Set());
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  const filterRef = useRef<HTMLDivElement>(null);

  /* ── Delete ── */
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  /* ── Row context menu ── */
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [editingLeadId, setEditingLeadId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const menuRef = useRef<HTMLDivElement>(null);

  /* ── Customize ── */
  const [topicGoalText, setTopicGoalText] = useState('');
  const [feedback, setFeedback] = useState('');
  const [feedbackHistory, setFeedbackHistory] = useState<string[]>([]);

  /* ── Preview ── */
  const [previews, setPreviews] = useState<EmailPreviewItem[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);

  /* ── Sender textareas ── */
  const [companyText, setCompanyText] = useState('');
  const [personText, setPersonText] = useState('');
  // senderSaving split into personSaving + companySaving (defined in save handlers)

  /* ── AI Research ── */
  const [researching, setResearching] = useState(false);
  const [researchUrl, setResearchUrl] = useState('');
  const [researchDone, setResearchDone] = useState(false);

  /* ── Topic textarea ── */
  const [topicText, setTopicText] = useState('');
  const [topicSaving, setTopicSaving] = useState(false);

  /* ── Derived ── */
  const senderCompanyText = tenant?.senderCompanyText || '';
  const senderPersonText = tenant?.senderPersonText || '';
  const senderName = tenant?.senderFullName || '';
  const companyDisplay = senderCompanyText || [tenant?.displayName, tenant?.companyDescription].filter(Boolean).join('\n');
  const personDisplay = senderPersonText || senderName || '';
  const senderConfigured = (companyDisplay.trim().length > 0 || personDisplay.trim().length > 0);
  const topicConfigured = topicGoalText.trim().length > 0;
  const recipientsConfigured = leads.length > 0;
  const allConfigured = senderConfigured && topicConfigured && recipientsConfigured;
  const goalDisplay = topicGoalText.trim() || 'Not configured';

  /* ── Init: load selectedGroupId from localStorage ── */
  useEffect(() => {
    const saved = typeof window !== 'undefined' ? sessionStorage.getItem('selectedGroupId') : null;
    if (saved) setSelectedGroupId(saved);
    setInitialLoad(false);
  }, []);

  /* ── Close dropdowns ── */
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setActiveFilter(null);
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenuId(null);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  /* ── Load tenant + group data when selectedGroupId changes ── */
  useEffect(() => {
    if (initialLoad) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const t = await apiGet<any>('/tenants/current');
        if (cancelled) return;
        setTenant(t);
        setResearchUrl(t.website || '');

        // Sender texts
        const ct = t.senderCompanyText || [t.displayName, t.companyDescription].filter(Boolean).join('\n');
        const pt = t.senderPersonText || t.senderFullName || '';
        setCompanyText(ct);
        setPersonText(pt);

        // Topic
        const lastGoal = typeof window !== 'undefined' ? localStorage.getItem('outreach_last_goal') : null;
        const effectiveGoal = t.topicGoalText || lastGoal || '';
        setTopicGoalText(effectiveGoal);
        setTopicText(effectiveGoal);

        // Load group if selected
        if (selectedGroupId) {
          try {
            const [g, l] = await Promise.all([
              getLeadGroup(selectedGroupId),
              getLeadGroupLeads(selectedGroupId),
            ]);
            if (cancelled) return;
            setGroup(g);
            setLeads(l as Lead[]);
            // Restore previous selection if available, otherwise select all
            const savedSelection = sessionStorage.getItem('draftSelectedLeads');
            if (savedSelection) {
              try {
                const savedIds: string[] = JSON.parse(savedSelection);
                const validIds = new Set((l as Lead[]).map((lead) => lead.id));
                setSelectedLeads(new Set(savedIds.filter((id) => validIds.has(id))));
              } catch {
                setSelectedLeads(new Set((l as Lead[]).map((lead) => lead.id)));
              }
            } else {
              setSelectedLeads(new Set((l as Lead[]).map((lead) => lead.id)));
            }
          } catch {
            // Group no longer exists — clear selection
            setSelectedGroupId(null);
            sessionStorage.removeItem('selectedGroupId');
            setGroup(null);
            setLeads([]);
          }
        } else {
          setGroup(null);
          setLeads([]);
        }
      } catch {
        toast.error('Failed to load data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedGroupId, initialLoad]);

  /* ── Select a group (from LeadGroupPicker) ── */
  function handleGroupSelect(groupId: string) {
    setSelectedGroupId(groupId);
    setPhase('idle');
    setDetailView('leads');
    setSelectedLeads(new Set());
    sessionStorage.removeItem('draftSelectedLeads');
    setColumnFilters({});
    setPreviews([]);
    setFeedback('');
    // Clear drafts flag when changing group
    if (typeof window !== 'undefined') sessionStorage.removeItem('draftsGenerated');
  }

  /* ── Save sender (individual fields) ── */
  const [personSaving, setPersonSaving] = useState(false);
  const [companySaving, setCompanySaving] = useState(false);

  async function handleSavePerson() {
    setPersonSaving(true);
    try {
      const person = personText.trim();
      const extractedName = person.split(',')[0].trim().split(/\s+/).slice(0, 4).join(' ');
      await apiPatch('/tenants/current', { senderPersonText: person, senderFullName: extractedName });
      setTenant((prev: any) => ({ ...prev, senderPersonText: person, senderFullName: extractedName }));
      toast.success('Personal info saved');
    } catch { toast.error('Failed to save'); }
    setPersonSaving(false);
  }

  async function handleSaveCompany() {
    setCompanySaving(true);
    try {
      const company = companyText.trim();
      await apiPatch('/tenants/current', { senderCompanyText: company });
      setTenant((prev: any) => ({ ...prev, senderCompanyText: company }));
      toast.success('Company info saved');
    } catch { toast.error('Failed to save'); }
    setCompanySaving(false);
  }

  /* ── AI Research ── */
  async function handleResearch() {
    if (!researchUrl.trim()) return;
    setResearching(true);
    setResearchDone(false);
    try {
      const data = await apiPost<any>('/ai/research-company', { url: researchUrl });
      const newCompanyText = [data.companyName, data.description].filter(Boolean).join('\n');
      setCompanyText(newCompanyText);
      await apiPatch('/tenants/current', { senderCompanyText: newCompanyText, displayName: data.companyName, companyDescription: data.description, website: researchUrl });
      setTenant((prev: any) => ({ ...prev, senderCompanyText: newCompanyText, displayName: data.companyName, website: researchUrl }));
      setResearchDone(true);
      toast.success('Company profile auto-filled');
    } catch (err: any) { toast.error(err.message || 'Research failed'); }
    setResearching(false);
  }

  /* ── Save topic ── */
  async function handleSaveTopic() {
    setTopicSaving(true);
    const text = topicText.trim();
    setTopicGoalText(text);
    await apiPatch('/tenants/current', { topicGoalText: text }).catch(() => toast.error('Failed to save goal'));
    if (typeof window !== 'undefined') localStorage.setItem('outreach_last_goal', text);
    toast.success('Topic & goal saved');
    setTopicSaving(false);
  }

  /* ── Delete group ── */
  async function handleDeleteGroup() {
    if (!group || !selectedGroupId) return;
    try {
      await deleteLeadGroup(selectedGroupId);
      toast.success(`Deleted "${group.name}"`);
      setSelectedGroupId(null);
      sessionStorage.removeItem('selectedGroupId');
    } catch { toast.error('Failed to delete group'); }
  }

  /* ── Delete lead ── */
  async function handleDeleteLead(id: string) {
    try {
      await apiDelete(`/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      setSelectedLeads((prev) => { const n = new Set(prev); n.delete(id); return n; });
    } catch { toast.error('Failed to delete lead'); }
  }

  function startEditLead(lead: Lead) {
    setEditingLeadId(lead.id);
    setEditValues({
      firstName: lead.firstName || '',
      lastName: lead.lastName || '',
      email: lead.email || '',
      company: lead.company || '',
      roleTitle: lead.roleTitle || '',
    });
    setOpenMenuId(null);
  }

  async function saveEditLead() {
    if (!editingLeadId) return;
    if (editValues.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editValues.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    try {
      await apiPatch(`/leads/${editingLeadId}`, editValues);
      setLeads((prev) => prev.map((l) => l.id === editingLeadId ? { ...l, ...editValues } : l));
      // Clear any stale preview for this lead so it gets regenerated with updated data
      setPreviews((prev) => prev.filter((p) => p.leadId !== editingLeadId));
      toast.success('Lead updated');
    } catch (err: any) { toast.error(err?.message || 'Failed to update lead'); }
    setEditingLeadId(null);
    setEditValues({});
  }

  async function handleBulkDelete() {
    const ids = Array.from(selectedLeads);
    let deleted = 0;
    for (const id of ids) { try { await apiDelete(`/leads/${id}`); deleted++; } catch {} }
    setLeads((prev) => prev.filter((l) => !selectedLeads.has(l.id)));
    setSelectedLeads(new Set());
    toast.success(`Deleted ${deleted} lead${deleted !== 1 ? 's' : ''}`);
  }

  function toggleLeadSelect(id: string) {
    setSelectedLeads((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }
  function toggleSelectAll() {
    setSelectedLeads(selectedLeads.size === filteredLeads.length ? new Set() : new Set(filteredLeads.map((l) => l.id)));
  }

  // Persist selection to sessionStorage so it survives navigation
  useEffect(() => {
    if (selectedLeads.size > 0) {
      sessionStorage.setItem('draftSelectedLeads', JSON.stringify(Array.from(selectedLeads)));
    }
  }, [selectedLeads]);

  /* ── Draft Emails click ── */
  function handleDraftEmailsClick() {
    if (!allConfigured) { setShowMissingDialog(true); return; }
    handleGeneratePreview();
  }

  /* ── Preview ── */
  async function handleGeneratePreview() {
    if (!selectedGroupId) return;
    setPhase('preview');
    setPreviewLoading(true);
    setPreviews([]);
    try {
      const selected = Array.from(selectedLeads);
      const targetIds = selected.length > 0 ? selected : filteredLeads.map((l) => l.id);
      const cappedIds = targetIds.slice(0, 3);
      const result = await generateEmailsPreview({ leadGroupId: selectedGroupId, topicGoalText, feedbackHistory: feedbackHistory.length > 0 ? feedbackHistory : undefined, sampleCount: cappedIds.length, leadIds: cappedIds });
      setPreviews(result.previews);
    } catch (err: any) { toast.error(err.message || 'Failed to generate previews'); }
    setPreviewLoading(false);
  }

  async function handleRegeneratePreview() {
    if (!selectedGroupId) return;
    // Accumulate feedback before regenerating
    const newHistory = [...feedbackHistory];
    if (feedback.trim()) {
      newHistory.push(feedback.trim());
      setFeedbackHistory(newHistory);
      setFeedback('');
    }
    setPreviewLoading(true);
    setPreviews([]);
    try {
      const selected = Array.from(selectedLeads);
      const targetIds = selected.length > 0 ? selected : filteredLeads.map((l) => l.id);
      const cappedIds = targetIds.slice(0, 3);
      const result = await generateEmailsPreview({ leadGroupId: selectedGroupId, topicGoalText, feedbackHistory: newHistory.length > 0 ? newHistory : undefined, sampleCount: cappedIds.length, leadIds: cappedIds });
      setPreviews(result.previews);
    } catch (err: any) { toast.error(err.message || 'Failed'); }
    setPreviewLoading(false);
  }

  /* ── Determine which leads to generate for ── */
  const generateTargetIds = selectedLeads.size > 0 ? Array.from(selectedLeads) : leads.map((l) => l.id);
  const generateCount = generateTargetIds.length;
  const isSubset = selectedLeads.size > 0 && selectedLeads.size < leads.length;

  async function handleGenerateAll() {
    if (!selectedGroupId) return;
    if (typeof window !== 'undefined') {
      if (topicGoalText) localStorage.setItem('outreach_last_goal', topicGoalText);
    }

    // Pass existing previews so they get saved as-is (not regenerated)
    const existingPreviews = previews.map((p) => ({
      leadId: p.leadId,
      subject: p.subject,
      body: p.body,
    }));

    // Check if all selected leads already have previews — skip batch API if so
    const previewedIds = new Set(previews.map((p) => p.leadId));
    const allCovered = generateTargetIds.every((id) => previewedIds.has(id));

    if (allCovered) {
      // All leads have previews — navigate immediately, save in background
      sessionStorage.setItem('draftsGenerated', 'true');
      sessionStorage.setItem('generatedLeadIds', JSON.stringify(generateTargetIds));
      // Pass preview data so ReviewSendPage can render instantly
      const previewMap: Record<string, { subject: string; body: string }> = {};
      for (const p of existingPreviews) {
        previewMap[p.leadId] = { subject: p.subject, body: p.body };
      }
      sessionStorage.setItem('previewEmailData', JSON.stringify(previewMap));
      // Carry forward feedback so ReviewSendPage can apply it to rewrites
      if (feedbackHistory.length > 0) {
        sessionStorage.setItem('draftFeedbackHistory', JSON.stringify(feedbackHistory));
      }
      navigate('/review-send');
      // Save to DB in background (ReviewSendPage will pick up DB data when it loads)
      generateEmailsBatch({
        leadGroupId: selectedGroupId, topicGoalText,
        leadIds: generateTargetIds,
        existingPreviews,
      }).catch(() => { /* ReviewSendPage will handle missing data gracefully */ });
      return;
    }

    // Some leads need generation — show progress
    setPhase('generating');
    setBatchGenerating(true);
    try {
      const batchResult = await generateEmailsBatch({
        leadGroupId: selectedGroupId, topicGoalText,
        feedbackHistory: feedbackHistory.length > 0 ? feedbackHistory : undefined,
        leadIds: generateTargetIds,
        existingPreviews,
      });
      setBatchGenerating(false);
      sessionStorage.setItem('draftsGenerated', 'true');
      sessionStorage.setItem('generatedLeadIds', JSON.stringify(generateTargetIds));
      // Carry forward feedback so ReviewSendPage can apply it to rewrites
      if (feedbackHistory.length > 0) {
        sessionStorage.setItem('draftFeedbackHistory', JSON.stringify(feedbackHistory));
      }
      // Pass failed lead info so ReviewSendPage can show inline indicators
      if (batchResult.failedLeads && batchResult.failedLeads.length > 0) {
        sessionStorage.setItem('failedLeads', JSON.stringify(batchResult.failedLeads));
        const failedNames = batchResult.failedLeads.map((f) => {
          const lead = leads.find((l) => l.id === f.leadId);
          return lead ? `${lead.firstName} ${lead.lastName}`.trim() : 'Unknown';
        });
        toast.error(`${batchResult.failed} email(s) failed: ${failedNames.join(', ')}`);
      }
      navigate('/review-send');
    } catch (err: any) {
      toast.error(err.message || 'Batch generation failed');
      setBatchGenerating(false);
      setPhase('preview');
    }
  }

  /* ── Column filter helpers ── */
  function getColValue(lead: Lead, col: string): string {
    if (col === 'name') return [lead.firstName, lead.lastName].filter(Boolean).join(' ') || lead.email.split('@')[0];
    if (col === 'source') return lead.sourceUrl ? new URL(lead.sourceUrl).hostname.replace('www.', '') : '—';
    if (col === 'company') return lead.company || '—';
    if (col === 'status') return lead.status === 'sent' ? 'Sent' : 'New';
    return '';
  }
  function getUniqueValues(col: string): string[] {
    const vals = new Set<string>();
    leads.forEach((lead) => vals.add(getColValue(lead, col)));
    return Array.from(vals).sort();
  }
  function openFilter(col: string) {
    if (activeFilter === col) { setActiveFilter(null); return; }
    setActiveFilter(col);
    setFilterSearch('');
    setPendingFilter(columnFilters[col] ? new Set(columnFilters[col]) : new Set(getUniqueValues(col)));
  }
  function applyFilter() {
    if (!activeFilter) return;
    const allVals = getUniqueValues(activeFilter);
    if (pendingFilter.size === allVals.length) {
      setColumnFilters((prev) => { const n = { ...prev }; delete n[activeFilter!]; return n; });
    } else {
      setColumnFilters((prev) => ({ ...prev, [activeFilter!]: new Set(pendingFilter) }));
    }
    setActiveFilter(null);
  }
  function handleSort(col: string, asc: boolean) { setSortCol(col); setSortAsc(asc); setActiveFilter(null); }

  const filteredLeads = leads
    .filter((lead) => { for (const [col, allowed] of Object.entries(columnFilters)) { if (allowed.size === 0) continue; if (!allowed.has(getColValue(lead, col))) return false; } return true; })
    .sort((a, b) => { if (!sortCol) return 0; return (sortAsc ? 1 : -1) * getColValue(a, sortCol).localeCompare(getColValue(b, sortCol)); });

  /* ── Draft variant ── */
  const draftNodeVariant: 'default' | 'cta' | 'cta-active' = phase === 'preview' ? 'cta-active' : 'cta';
  const showCheckmarks = phase === 'preview';

  if (initialLoad || loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-primary" /></div>;
  }

  /* ── Filter column dropdown (reusable) ── */
  function FilterDropdown({ col, label, width, align = 'left' }: { col: string; label: string; width: string; align?: 'left' | 'right' }) {
    return (
      <div className={`${width} relative`}>
        <button onClick={() => openFilter(col)} className={`flex items-center gap-1 hover:text-base-content/50 transition-colors ${columnFilters[col] ? 'text-primary' : ''}`}>
          {label} <ListFilter size={8} />
          {columnFilters[col] && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
        </button>
        {activeFilter === col && (
          <div className={`absolute top-full ${align === 'right' ? 'right-0' : 'left-0'} mt-1 w-56 bg-base-100 border border-base-300/30 rounded-xl shadow-2xl z-50 animate-fade-in overflow-hidden`}>
            <button onClick={() => handleSort(col, true)} className="w-full px-4 py-2 text-left text-xs hover:bg-base-200/50 flex items-center gap-2">Sort A → Z</button>
            <button onClick={() => handleSort(col, false)} className="w-full px-4 py-2 text-left text-xs hover:bg-base-200/50 flex items-center gap-2">Sort Z → A</button>
            <div className="border-t border-base-content/5 mx-2 my-1" />
            <div className="px-4 py-2">
              <p className="text-[10px] font-semibold text-base-content/40 mb-1.5">Filter by values</p>
              <div className="flex items-center gap-2 mb-2 text-[10px]">
                <button onClick={() => setPendingFilter(new Set(getUniqueValues(col)))} className="text-primary hover:underline">Select all</button>
                <span className="text-base-content/20">&middot;</span>
                <button onClick={() => setPendingFilter(new Set())} className="text-primary hover:underline">Clear</button>
                <span className="text-base-content/20 ml-auto">{pendingFilter.size} selected</span>
              </div>
              <div className="max-h-32 overflow-y-auto space-y-0.5">
                {getUniqueValues(col).filter((v) => !filterSearch || v.toLowerCase().includes(filterSearch.toLowerCase())).map((val) => (
                  <label key={val} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-base-200/30 cursor-pointer">
                    <input type="checkbox" className="checkbox checkbox-xs checkbox-primary" checked={pendingFilter.has(val)}
                      onChange={() => setPendingFilter((prev) => { const n = new Set(prev); if (n.has(val)) n.delete(val); else n.add(val); return n; })} />
                    <span className="text-xs truncate">{val}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-2 border-t border-base-content/5 bg-base-200/20">
              <button onClick={() => setActiveFilter(null)} className="btn btn-ghost btn-xs">Cancel</button>
              <button onClick={applyFilter} className="btn btn-primary btn-xs">OK</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[50px] max-w-6xl mx-auto w-full animate-fade-in">
      {/* ── Formula Bar ── */}
      <div className="flex items-end gap-0 mb-4 w-full justify-between">
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-base-content/30 mb-2">Sender</span>
          <FormulaNode icon={User}
            title={senderConfigured ? (senderName || personDisplay.split(',')[0] || 'Sender') : 'Set up sender'}
            subtitle={senderConfigured ? (showCheckmarks ? undefined : 'Configured') : 'Click to configure'}
            subtitleCheck={!showCheckmarks && senderConfigured}
            accentColor="gray"
            onClick={() => { setDetailView('sender'); setPhase('idle'); }}
            active={detailView === 'sender' && phase === 'idle'}
            checkmark={showCheckmarks && senderConfigured}
            emptyPlaceholder={!senderConfigured}
            showPen={false}
          />
        </div>
        <FormulaOperator symbol="+" />
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-base-content/30 mb-2">Topic/Goal</span>
          <FormulaNode icon={Megaphone}
            title={topicConfigured ? goalDisplay : 'Set topic/goal'}
            subtitle={topicConfigured ? (showCheckmarks ? undefined : 'Goal set') : 'Click to configure'}
            subtitleCheck={!showCheckmarks && topicConfigured}
            accentColor="gray"
            onClick={() => { setDetailView('topic'); setPhase('idle'); }}
            active={detailView === 'topic' && phase === 'idle'}
            checkmark={showCheckmarks && topicConfigured}
            emptyPlaceholder={!topicConfigured}
            showPen={false}
          />
        </div>
        <FormulaOperator symbol="+" />
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-base-content/30 mb-2">Recipients</span>
          <FormulaNode icon={Users}
            title={recipientsConfigured ? (group?.name || 'Recipients') : 'Select a list'}
            subtitle={recipientsConfigured ? (showCheckmarks ? undefined : `${leads.length} lead${leads.length !== 1 ? 's' : ''}`) : 'Click to choose'}
            subtitleCheck={!showCheckmarks && recipientsConfigured}
            accentColor={recipientsConfigured ? 'blue' : 'gray'}
            onClick={() => {
              if (detailView === 'leads' && phase === 'idle') setDetailView('pick-list');
              else { setDetailView(recipientsConfigured ? 'leads' : 'pick-list'); setPhase('idle'); }
            }}
            active={(detailView === 'leads' || detailView === 'pick-list') && phase === 'idle'}
            badge={showCheckmarks ? undefined : (recipientsConfigured ? leads.length : undefined)}
            checkmark={showCheckmarks && recipientsConfigured}
            emptyPlaceholder={!recipientsConfigured}
          />
        </div>
        <FormulaOperator symbol="=" />
        <div className="flex flex-col items-center">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-transparent mb-2 select-none">&middot;</span>
          <FormulaNode icon={Sparkles} title="Draft Emails" accentColor="gray" onClick={handleDraftEmailsClick} variant={draftNodeVariant} />
        </div>
      </div>

      {/* ── Phase: idle ── */}
      {phase === 'idle' && (
        <div className="animate-fade-in">
          {/* Sender Panel */}
          {detailView === 'sender' && (
            <div className="space-y-4">
              <div className="glass-card rounded-xl p-5 space-y-3">
                <div className="flex items-center gap-2 mb-1"><User size={16} className="text-base-content/40" /><p className="text-sm font-semibold">Personal Information</p></div>
                <textarea value={personText} onChange={(e) => setPersonText(e.target.value)}
                  placeholder="e.g. Dr. Marcus Kraft MRICS, Board Member"
                  onInput={(e) => { const el = e.target as HTMLTextAreaElement; el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }}
                  ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                  className="textarea textarea-bordered w-full text-sm leading-relaxed min-h-[120px] resize-y overflow-hidden" />
                <div className="flex justify-end">
                  <button onClick={handleSavePerson} disabled={personSaving || personText.trim() === personDisplay.trim()} className="btn btn-primary btn-sm gap-1">
                    {personSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
                  </button>
                </div>
              </div>
              <div className="glass-card rounded-xl p-5 space-y-3">
                <div className="flex items-center gap-2 mb-1"><Building2 size={16} className="text-base-content/40" /><p className="text-sm font-semibold">Company Information</p></div>
                <textarea value={companyText} onChange={(e) => setCompanyText(e.target.value)}
                  placeholder="e.g. RECON AG — specialized real estate investment consulting"
                  onInput={(e) => { const el = e.target as HTMLTextAreaElement; el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }}
                  ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                  className="textarea textarea-bordered w-full text-sm leading-relaxed min-h-[120px] resize-y overflow-hidden" />
                <div className="flex justify-end">
                  <button onClick={handleSaveCompany} disabled={companySaving || companyText.trim() === companyDisplay.trim()} className="btn btn-primary btn-sm gap-1">
                    {companySaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
                  </button>
                </div>
              </div>
              <div className="glass-card rounded-xl p-5 space-y-3">
                <div className="flex items-center gap-2"><Sparkles size={16} className="text-primary" /><p className="text-sm font-semibold">Auto-fill with AI</p></div>
                <p className="text-xs text-base-content/40">Enter your website and we auto-fill your company profile.</p>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Globe size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/30" />
                    <input type="url" value={researchUrl} onChange={(e) => setResearchUrl(e.target.value)} placeholder="https://yourcompany.com" className="input input-bordered input-sm w-full pl-9" onKeyDown={(e) => e.key === 'Enter' && handleResearch()} />
                  </div>
                  <button onClick={handleResearch} disabled={researching || !researchUrl.trim()} className="btn btn-primary btn-sm gap-1.5">
                    {researching ? <><Loader2 size={14} className="animate-spin" /> Researching...</> : researchDone ? <><Check size={14} /> Done</> : <><Sparkles size={14} /> Research</>}
                  </button>
                </div>
                {researching && <AIProgressBar active={true} size="sm" steps={['Reading your website...', 'Extracting company info...', 'Building profile...']} />}
              </div>
            </div>
          )}

          {/* Topic Panel */}
          {detailView === 'topic' && (
            <div className="glass-card rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 mb-1"><Megaphone size={16} className="text-base-content/40" /><p className="text-sm font-semibold">Topic & Goal</p></div>
              <textarea value={topicText} onChange={(e) => setTopicText(e.target.value)}

                placeholder="e.g. Introduce our consulting services to restaurant owners, book a short call"
                onInput={(e) => { const el = e.target as HTMLTextAreaElement; el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }}
                ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                className="textarea textarea-bordered w-full text-sm leading-relaxed min-h-[120px] resize-y overflow-hidden" />
              <div className="flex justify-end">
                <button onClick={handleSaveTopic} disabled={topicSaving || topicText.trim() === topicGoalText.trim()} className="btn btn-primary btn-sm gap-1">
                  {topicSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
                </button>
              </div>
            </div>
          )}

          {/* Lead List Panel */}
          {detailView === 'leads' && (recipientsConfigured || loading) && (
            <div className="glass-card rounded-xl overflow-hidden relative">
              <div className="flex items-center gap-2 px-5 py-3 border-b border-base-content/5">
                <Users size={16} className="text-base-content/40" />
                <p className="text-sm font-semibold">Lead List</p>
                <span className="badge badge-sm badge-ghost ml-1">{leads.length}</span>
                {selectedLeads.size > 0 && (
                  <span className="text-[10px] text-primary font-medium">{selectedLeads.size} of {filteredLeads.length} selected</span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {selectedLeads.size > 0 && <button onClick={handleBulkDelete} className="btn btn-ghost btn-xs text-error gap-1"><Trash2 size={10} /> Delete {selectedLeads.size}</button>}
                  <button onClick={() => setShowDeleteConfirm(true)} className="btn btn-ghost btn-xs text-base-content/20 hover:text-error gap-1"><Trash2 size={10} /> Delete List</button>
                </div>
              </div>
              {Object.keys(columnFilters).length > 0 && (
                <div className="flex items-center gap-2 px-5 py-1.5 border-b border-base-content/5 bg-primary/5 flex-wrap">
                  <span className="text-[10px] text-primary font-medium">Filtered: {filteredLeads.length} of {leads.length}</span>
                  {Object.entries(columnFilters).map(([col, vals]) => (
                    <span key={col} className="inline-flex items-center gap-1 text-[10px] bg-primary/10 text-primary rounded-full px-2 py-0.5">
                      {col}: {vals.size} value{vals.size !== 1 ? 's' : ''}
                      <button onClick={() => setColumnFilters((f) => { const n = { ...f }; delete n[col]; return n; })} className="hover:text-primary/60">&times;</button>
                    </span>
                  ))}
                  <button onClick={() => setColumnFilters({})} className="text-[10px] text-primary/60 hover:text-primary underline">Clear all</button>
                </div>
              )}
              {leads.length === 0 ? (
                <p className="text-sm text-base-content/40 text-center py-8">No leads in this list.</p>
              ) : (
                <>
                  <div className="flex items-center gap-4 px-6 py-3 border-b border-base-content/5 text-[10px] font-semibold uppercase tracking-wider text-base-content/30" ref={filterRef}>
                    <div className="w-8 shrink-0 flex items-center justify-center">
                      <input type="checkbox" className="checkbox checkbox-sm checkbox-primary" checked={selectedLeads.size === filteredLeads.length && filteredLeads.length > 0} onChange={toggleSelectAll} />
                    </div>
                    <FilterDropdown col="name" label="Name" width="w-40" />
                    <FilterDropdown col="source" label="Source" width="w-40" />
                    <FilterDropdown col="company" label="Company" width="w-32" />
                    <div className="w-48">Email</div>
                    <FilterDropdown col="status" label="Status" width="w-16" align="right" />
                    <div className="w-8" />
                  </div>
                  <div className="divide-y divide-base-content/5">
                    {filteredLeads.map((lead) => {
                      const name = (lead.firstName || lead.lastName) ? [lead.firstName, lead.lastName].filter(Boolean).join(' ') : null;
                      const isEditing = editingLeadId === lead.id;

                      if (isEditing) {
                        return (
                          <div key={lead.id} className="flex items-center gap-4 px-6 py-3 bg-primary/5 border-l-2 border-l-primary">
                            <div className="w-8 shrink-0" />
                            <div className="w-40 min-w-0 flex gap-1">
                              <input value={editValues.firstName || ''} onChange={(e) => setEditValues((v) => ({ ...v, firstName: e.target.value }))} placeholder="First" className="input input-xs input-bordered w-1/2 text-xs" />
                              <input value={editValues.lastName || ''} onChange={(e) => setEditValues((v) => ({ ...v, lastName: e.target.value }))} placeholder="Last" className="input input-xs input-bordered w-1/2 text-xs" />
                            </div>
                            <div className="w-40 min-w-0">
                              <input value={editValues.roleTitle || ''} onChange={(e) => setEditValues((v) => ({ ...v, roleTitle: e.target.value }))} placeholder="Role / Title" className="input input-xs input-bordered w-full text-xs" />
                            </div>
                            <div className="w-32 min-w-0">
                              <input value={editValues.company || ''} onChange={(e) => setEditValues((v) => ({ ...v, company: e.target.value }))} placeholder="Company" className="input input-xs input-bordered w-full text-xs" />
                            </div>
                            <div className="w-48 min-w-0">
                              <input value={editValues.email || ''} onChange={(e) => setEditValues((v) => ({ ...v, email: e.target.value }))} placeholder="Email" className={`input input-xs input-bordered w-full text-xs ${editValues.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editValues.email) ? 'input-error' : ''}`} />
                            </div>
                            <div className="w-16 shrink-0" />
                            <div className="w-8 shrink-0 flex items-center gap-1">
                              <button onClick={saveEditLead} className="btn btn-ghost btn-xs btn-circle text-green-500"><Check size={12} /></button>
                              <button onClick={() => setEditingLeadId(null)} className="btn btn-ghost btn-xs btn-circle text-base-content/30">✕</button>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={lead.id} className="flex items-center gap-4 px-6 py-4 hover:bg-base-200/30 transition-colors group">
                          <div className="w-8 shrink-0 flex items-center justify-center">
                            <input type="checkbox" className="checkbox checkbox-sm checkbox-primary" checked={selectedLeads.has(lead.id)} onChange={() => toggleLeadSelect(lead.id)} />
                          </div>
                          <div className="w-40 min-w-0 text-sm font-medium truncate">
                            {name || <span className="text-base-content/40">{lead.email.split('@')[0]}</span>}
                          </div>
                          <div className="w-40 min-w-0 truncate text-sm">
                            {lead.sourceUrl ? (
                              <a href={lead.sourceUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                                {lead.sourceUrl.includes('linkedin.com') ? <Linkedin size={12} /> : <ExternalLink size={12} />}
                                <span className="truncate">{new URL(lead.sourceUrl).hostname.replace('www.', '')}</span>
                              </a>
                            ) : <span className="text-base-content/20">—</span>}
                          </div>
                          <div className="w-32 min-w-0 truncate text-sm text-base-content/60">{lead.company || '—'}</div>
                          <div className="w-48 min-w-0 truncate text-sm text-base-content/40">{lead.email}</div>
                          <div className="w-16 shrink-0 flex items-center justify-end">
                            {lead.status === 'sent' ? <span className="flex items-center gap-1 text-xs text-green-500 font-medium"><CheckCircle2 size={12} /> Sent</span> : <span className="text-xs text-base-content/20">New</span>}
                          </div>
                          <div className="w-8 shrink-0 flex items-center justify-center relative" ref={openMenuId === lead.id ? menuRef : undefined}>
                            <button
                              onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === lead.id ? null : lead.id); }}
                              className="opacity-0 group-hover:opacity-100 transition-opacity btn btn-ghost btn-xs btn-circle text-base-content/30 hover:text-base-content/60"
                            >
                              <MoreVertical size={14} />
                            </button>
                            {openMenuId === lead.id && (
                              <div className="absolute top-full right-0 mt-1 w-36 bg-base-100 border border-base-300/30 rounded-lg shadow-xl z-50 animate-fade-in overflow-hidden">
                                <button
                                  onClick={(e) => { e.stopPropagation(); startEditLead(lead); }}
                                  className="w-full px-3 py-2 text-left text-xs hover:bg-base-200/50 flex items-center gap-2 text-base-content/70"
                                >
                                  <PenLine size={12} /> Edit
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); handleDeleteLead(lead.id); }}
                                  className="w-full px-3 py-2 text-left text-xs hover:bg-base-200/50 flex items-center gap-2 text-error/70 hover:text-error"
                                >
                                  <Trash2 size={12} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Lead Group Picker */}
          {(detailView === 'pick-list' || (detailView === 'leads' && !recipientsConfigured && !loading)) && (
            <LeadGroupPicker currentGroupId={selectedGroupId || undefined} onSelect={handleGroupSelect} />
          )}

          {/* Welcome / default view */}
          {detailView === null && (
            <div className="glass-card rounded-xl p-8 text-center space-y-4 max-w-lg mx-auto">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                <Sparkles size={24} className="text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Welcome to Draft Emails</h2>
                <p className="text-sm text-base-content/50 mt-2 leading-relaxed">
                  Configure the formula above to generate personalized emails. Click each node to set it up:
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                {!senderConfigured && (
                  <button onClick={() => setDetailView('sender')} className="w-[220px] h-[68px] rounded-xl px-4 py-3 border-2 border-dashed border-base-content/20 hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer flex items-center gap-2 text-left">
                    <div className="w-7 h-7 rounded-full border-2 border-dashed border-primary/40 flex items-center justify-center shrink-0">
                      <span className="text-primary text-lg leading-none">+</span>
                    </div>
                    <span className="text-sm font-semibold text-base-content/40">Set Up Sender</span>
                  </button>
                )}
                {!topicConfigured && (
                  <button onClick={() => setDetailView('topic')} className="w-[220px] h-[68px] rounded-xl px-4 py-3 border-2 border-dashed border-base-content/20 hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer flex items-center gap-2 text-left">
                    <div className="w-7 h-7 rounded-full border-2 border-dashed border-primary/40 flex items-center justify-center shrink-0">
                      <span className="text-primary text-lg leading-none">+</span>
                    </div>
                    <span className="text-sm font-semibold text-base-content/40">Define Topic & Goal</span>
                  </button>
                )}
                {!recipientsConfigured && (
                  <button onClick={() => setDetailView('pick-list')} className="w-[220px] h-[68px] rounded-xl px-4 py-3 border-2 border-dashed border-base-content/20 hover:border-primary/40 hover:shadow-sm transition-all cursor-pointer flex items-center gap-2 text-left">
                    <div className="w-7 h-7 rounded-full border-2 border-dashed border-primary/40 flex items-center justify-center shrink-0">
                      <span className="text-primary text-lg leading-none">+</span>
                    </div>
                    <span className="text-sm font-semibold text-base-content/40">Select a Lead List</span>
                  </button>
                )}
                {allConfigured && (
                  <p className="text-sm text-success text-center py-2">All configured — click Draft Emails to generate!</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Phase: preview ── */}
      {phase === 'preview' && (
        <div className="space-y-5 max-w-3xl mx-auto animate-fade-in">
          {previewLoading ? (
            <div className="glass-card rounded-xl p-8">
              <AIProgressBar active={true} size="lg" steps={['Analyzing lead profiles...', 'Crafting personalized subject lines...', 'Writing sample email bodies...', 'Personalizing for each recipient...', 'Preparing preview samples...']} />
              <p className="text-sm text-base-content/40 text-center mt-4">
                Generating {Math.min(selectedLeads.size > 0 ? selectedLeads.size : filteredLeads.length, 3)} preview email{Math.min(selectedLeads.size > 0 ? selectedLeads.size : filteredLeads.length, 3) === 1 ? '' : 's'}...
              </p>
            </div>
          ) : previews.length > 0 ? (
            <>
              <p className="text-xs font-semibold uppercase tracking-wider text-base-content/40">Preview ({previews.length} {previews.length === 1 ? 'email' : 'emails'})</p>
              {previews.map((preview, idx) => {
                const recipientName = [preview.lead.firstName, preview.lead.lastName].filter(Boolean).join(' ') || preview.lead.email;
                const recipientInitial = (preview.lead.firstName?.[0] || preview.lead.email[0] || '?').toUpperCase();
                return (
                  <div key={idx} className="bg-base-100 border border-base-300/30 rounded-xl overflow-hidden shadow-sm">
                    <div className="px-5 py-3 border-b border-base-300/20 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center text-primary font-bold text-sm shrink-0 mt-0.5">{recipientInitial}</div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2"><p className="text-sm font-semibold truncate">{recipientName}</p>{preview.lead.company && <span className="text-[10px] text-base-content/30">&middot; {preview.lead.company}</span>}</div>
                        <p className="text-xs text-base-content/40 truncate">To: {preview.lead.email}</p>
                      </div>
                    </div>
                    <div className="px-5 py-2 border-b border-base-300/10"><p className="text-sm font-medium">{preview.subject}</p></div>
                    <div className="px-5 py-4"><p className="text-sm text-base-content/70 leading-relaxed whitespace-pre-line">{preview.body}</p></div>
                  </div>
                );
              })}
              {/* Feedback history */}
              {feedbackHistory.length > 0 && (
                <div className="flex flex-wrap gap-1.5 px-1">
                  {feedbackHistory.map((fb, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                      {fb}
                      <button onClick={() => setFeedbackHistory((prev) => prev.filter((_, idx) => idx !== i))} className="hover:text-error ml-0.5">&times;</button>
                    </span>
                  ))}
                </div>
              )}

              {/* Feedback input */}
              <div className="bg-base-200/50 border border-base-300/60 rounded-xl px-5 py-4 flex items-center gap-3">
                <PenLine size={16} className="text-base-content/40 shrink-0" />
                <input type="text" value={feedback} onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Tell the AI what to change — e.g. shorter, mention their product launch, softer CTA..."
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-base-content/40"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !previewLoading) handleRegeneratePreview(); }} />
                <button onClick={handleRegeneratePreview} disabled={previewLoading || !feedback.trim()} className="btn btn-ghost btn-sm gap-1.5 shrink-0">
                  {previewLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Rewrite
                </button>
              </div>
              <div className="flex items-center justify-between">
                <button onClick={() => { setPhase('idle'); setDetailView('leads'); }} className="btn btn-ghost btn-sm gap-1"><ArrowLeft size={14} /> Back</button>
                <button onClick={handleGenerateAll}
                  className="w-[220px] h-[68px] rounded-xl px-4 py-3 border-l-4 border-l-primary bg-gradient-to-r from-primary to-purple-500 text-white shadow-md hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] transition-all flex items-center gap-2 cursor-pointer">
                  <Sparkles size={18} className="text-white shrink-0" /><span className="text-sm font-semibold">{isSubset ? `Generate for ${generateCount} selected` : `Generate for all ${generateCount} leads`}</span>
                </button>
              </div>
            </>
          ) : (
            <div className="glass-card rounded-xl p-8 text-center">
              <p className="text-sm text-base-content/40">No previews generated yet.</p>
              <button onClick={() => { setPhase('idle'); setDetailView('leads'); }} className="btn btn-ghost btn-sm gap-1 mt-3"><ArrowLeft size={14} /> Back</button>
            </div>
          )}
        </div>
      )}

      {/* ── Phase: generating ── */}
      {phase === 'generating' && (
        <div className="flex items-center justify-center min-h-[40vh] animate-fade-in">
          <div className="glass-card rounded-2xl p-8 w-full max-w-md mx-4 space-y-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto"><Sparkles size={28} className="text-primary" /></div>
            <div className="space-y-2">
              <h2 className="text-lg font-bold">Generating emails</h2>
              <p className="text-sm text-base-content/40">Generating emails for {generateCount} lead{generateCount !== 1 ? 's' : ''}...</p>
            </div>
            <AIProgressBar active={batchGenerating} size="lg" steps={['Analyzing lead profiles...', 'Crafting personalized subject lines...', 'Writing email body for each lead...', 'Personalizing for each recipient...', 'Finalizing email drafts...']} />
          </div>
        </div>
      )}

      <ConfirmDialog open={showDeleteConfirm} title="Delete lead list?"
        message={`Are you sure you want to delete "${group?.name}"? All leads will be removed.`}
        onConfirm={() => { setShowDeleteConfirm(false); handleDeleteGroup(); }}
        onCancel={() => setShowDeleteConfirm(false)} />

      {showMissingDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-base-100/80" onClick={() => setShowMissingDialog(false)} />
          <div className="relative bg-base-100 border border-base-300/30 rounded-2xl p-6 w-full max-w-sm mx-4 space-y-4 animate-fade-in shadow-2xl">
            <h3 className="text-sm font-bold">Before drafting emails</h3>
            <p className="text-xs text-base-content/40">Complete the following to generate email drafts:</p>
            <div className="space-y-2">
              {!senderConfigured && <button onClick={() => { setShowMissingDialog(false); setDetailView('sender'); setPhase('idle'); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-base-300/30 hover:bg-base-200/30 transition-colors text-left"><User size={16} className="text-warning shrink-0" /><span className="text-sm">Configure your sender profile</span></button>}
              {!topicConfigured && <button onClick={() => { setShowMissingDialog(false); setDetailView('topic'); setPhase('idle'); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-base-300/30 hover:bg-base-200/30 transition-colors text-left"><Megaphone size={16} className="text-warning shrink-0" /><span className="text-sm">Set a topic or goal</span></button>}
              {!recipientsConfigured && <button onClick={() => { setShowMissingDialog(false); setDetailView('pick-list'); setPhase('idle'); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-base-300/30 hover:bg-base-200/30 transition-colors text-left"><Users size={16} className="text-warning shrink-0" /><span className="text-sm">Select a lead list</span></button>}
            </div>
            <button onClick={() => setShowMissingDialog(false)} className="btn btn-ghost btn-sm w-full">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
